import { Icon } from '../../components/icon';
import { addStyles, assetPath, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import type { NavDrawerCtrl, NavDrawerEntry } from './nav.drawer.ctrl';

const CSS = {
	container: 'c-Nav',
	isVisible: 'c-Nav--isVisible',
	content: 'c-Nav__Content',
	logo: 'c-Nav__Logo',
	username: 'c-Nav__UserName',
	navText: 'c-Nav__NavText',
	logout: 'c-Nav__Logout',
	entry: 'c-Nav__Entry',
	entryCurrent: 'c-Nav__Entry--Current',
	bullet: 'c-Nav__Bullet',
};

const Style = {
	[CSS.container]: {
		'justify-content': 'space-between',
		'overflow-x': 'hidden',
	},
	[CSS.isVisible]: {
		transform: 'translateX(0)',
	},
	[CSS.content]: {
		display: 'flex',
		'flex-direction': 'column',
	},
	[CSS.logo]: {
		'margin-top': DefaultTheme.space(4),
		width: '100%',
		height: '50px',
	},
	[CSS.username]: {
		'text-align': 'center',
		margin: `${DefaultTheme.space(4)} 0`,
		overflow: 'hidden',
		'text-overflow': 'ellipsis',
		'white-space': 'nowrap',
	},
	[CSS.navText]: {
		'text-align': 'left',
		margin: `${DefaultTheme.space(1)}`,
		overflow: 'visible',
		'white-space': 'wrap',
	},
	[CSS.logout]: {
		display: 'inline-block',
		padding: `0 0 ${DefaultTheme.space(4)} ${DefaultTheme.space(4)}`,
	},
	[CSS.entry]: {
		height: DefaultTheme.space(18),
		display: 'flex',
		'align-items': 'center',
		color: '#757575',
		'font-weight': 700,
		cursor: 'pointer',
		'text-decoration': 'none',
		'&:hover': {
			'background-color': DefaultTheme.background.lightGrey,
			color: DefaultTheme.color.accent,
		},
	},
	[CSS.entryCurrent]: {
		cursor: 'default',
		color: DefaultTheme.color.accent,
	},
	[CSS.bullet]: {
		width: DefaultTheme.space(18),
		'text-align': 'center',
	},
};

addStyles(Style);

export const NavDrawer = {
	view(ctrl: NavDrawerCtrl): MithrilVNode {
		const entryNodes: MithrilVNode[] = [];
		for (const entry of ctrl.entries) {
			entryNodes.push(NavDrawer.viewEntry(ctrl, entry));
		}

		const visibleClass = ctrl.visible ? CSS.isVisible : '';
		return m(
			'div',
			{
				className: `${CSS.container} mdl-layout__drawer ${visibleClass}`,
				oncreate: (vnode: MithrilVNode) => Services.FocusAway.from(vnode.dom, () => ctrl.setVisible(false)),
			},
			[
				m('div', { className: CSS.content }, [
					m('img', { className: CSS.logo, src: assetPath('/images/ike_logo.svg') }),
					m('span', { className: CSS.username }, ctrl.username),
					...entryNodes,
				]),
				m('div', { className: CSS.content }, [
					m('a', { className: CSS.logout, href: Services.Route.getRouteURL(IkePageName.logout) }, Translate(T.LOGOUT)),
				]),
			]
		);
	},

	viewEntry(ctrl: NavDrawerCtrl, entry: NavDrawerEntry): MithrilVNode {
		if (entry.current) {
			return m(
				'a',
				{
					className: `${CSS.entry} ${CSS.entryCurrent}`,
				},
				[
					Icon.view(entry.icon, { className: CSS.bullet }),
					m('span', { className: CSS.navText }, Translate(entry.title)),
				]
			);
		}

		return m(
			'a',
			{
				className: CSS.entry,
				href: Services.Route.getRouteURL(entry.name),
				target: entry.type === 'external' ? '_blank' : undefined,
				onclick:
					entry.type === 'internal'
						? async (e) => {
								await Services.Window.confirmPageChange(e, Services.Route.getRouteURL(entry.name));
							}
						: undefined,
			},
			[Icon.view(entry.icon, { className: CSS.bullet }), m('span', { className: CSS.navText }, Translate(entry.title))]
		);
	},
};
